import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  searchPricingCustomers,
  listPricingProfiles,
  listCustomerPrices,
  listProfilePrices,
  listProfileCustomers,
  getCustomerPricingContext,
  getCustomerPriceListStatus,
} from '../../services/pricingApi';
import { PageHeader, Card, Tabs, TabPanel, Badge, StatusBadge, SearchInput, Button } from '../ui';
import useDebouncedValue from '../../hooks/useDebouncedValue';
import { TypeAhead } from './PriceLookup';

const fmtMoney = (n) => {
  const num = Number(n);
  return Number.isFinite(num)
    ? num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : '—';
};
const fmt = (n) => {
  const num = Number(n);
  return Number.isFinite(num) ? num.toLocaleString() : (n ?? '—');
};
const fmtTime = (t) => (t ? new Date(t).toLocaleString() : '—');

const TABS = [
  { id: 'customer', label: 'By Customer' },
  { id: 'profile', label: 'By Profile' },
];

const PAGE_SIZE = 50;

/* Shared paginated resolved-price table (customer and profile views). */
const PriceTable = ({ data, page, setPage, search, setSearch, loading }) => (
  <>
    <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 10 }}>
      <SearchInput
        value={search}
        onChange={(v) => { setPage(1); setSearch(v); }}
        placeholder="Filter by SKU — wildcards * and ? (244*, 244?1)"
      />
      {data && <span className="muted">{fmt(data.total)} products</span>}
    </div>
    <table>
      <thead>
        <tr>
          <th>SKU</th>
          <th>Description</th>
          <th>Unit price</th>
          <th>List price</th>
          <th>Off list</th>
          <th>Qty tiers</th>
          <th>Price source</th>
          <th>Discount</th>
        </tr>
      </thead>
      <tbody>
        {loading && (
          <tr><td colSpan={8} className="muted">Loading…</td></tr>
        )}
        {!loading && !data?.prices?.length && (
          <tr><td colSpan={8} className="muted">No resolved prices for this selection.</td></tr>
        )}
        {!loading && (data?.prices || []).map((r) => {
          const master = Number(r.master_price);
          const unit = Number(r.unit_price);
          const offList = master > 0 && Number.isFinite(unit)
            ? `${(100 * (1 - unit / master)).toFixed(1)}%`
            : '—';
          const tiers = Array.isArray(r.tiers) ? r.tiers : [];
          return (
            <tr key={r.part_num}>
              <td><strong>{r.part_num}</strong></td>
              <td className="muted" style={{ maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {r.part_description || '—'}
              </td>
              <td><strong>{fmtMoney(r.unit_price)}</strong></td>
              <td>{fmtMoney(r.master_price)}</td>
              <td>{offList}</td>
              <td>
                {tiers.filter((t) => Number(t.min_qty) > 1).map((t) => (
                  <span key={t.min_qty} className="chip" style={{ marginRight: 6 }}>
                    {t.min_qty}+ → {fmtMoney(t.unit_price)}
                  </span>
                ))}
                {tiers.filter((t) => Number(t.min_qty) > 1).length === 0 && '—'}
              </td>
              <td className="muted">
                {r.price_source === 'part_master'
                  ? 'Part master'
                  : `${r.price_list_code || ''} (${r.price_source || '—'})`}
              </td>
              <td className="muted">
                {r.discount_percent != null
                  ? `${Number(r.discount_percent)}%${r.disc_list_code ? ` via ${r.disc_list_code}` : ''}`
                  : '—'}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
    {data && data.total > PAGE_SIZE && (
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8 }}>
        <Button variant="secondary" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>
          ‹ Prev
        </Button>
        <span className="muted">
          Page {data.page} of {Math.max(1, Math.ceil(data.total / PAGE_SIZE))}
        </span>
        <Button
          variant="secondary"
          size="sm"
          disabled={page * PAGE_SIZE >= data.total}
          onClick={() => setPage(page + 1)}
        >
          Next ›
        </Button>
      </div>
    )}
  </>
);

/* BC push status strip for one customer (all environments). */
const BcStatusStrip = ({ status }) => {
  if (!status) return null;
  const rows = status.price_lists || [];
  const mappings = status.bc_mappings || [];
  return (
    <div style={{ marginTop: 10 }}>
      <h4 style={{ margin: '0 0 6px' }}>BigCommerce price list status</h4>
      {rows.length === 0 && (
        <p className="muted" style={{ margin: 0 }}>
          Never pushed to BigCommerce yet
          {mappings.length === 0 ? ' — and no BC customer mapping exists for this customer.' : '.'}
        </p>
      )}
      {rows.length > 0 && (
        <table style={{ maxWidth: 760 }}>
          <thead>
            <tr>
              <th>Target</th>
              <th>BC price list</th>
              <th>BC customer group</th>
              <th>Last pushed</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={`${r.environment}-${r.store_code}`}>
                <td>{r.store_code} {r.environment === 'staging' ? 'Staging' : 'Production'}</td>
                <td>{r.bc_price_list_id ?? '—'}</td>
                <td>{r.bc_customer_group_id ?? '—'}</td>
                <td>{fmtTime(r.last_pushed_at)}</td>
                <td>
                  <StatusBadge
                    status={r.last_push_status === 'ok' ? 'success' : r.last_push_status?.startsWith('error') ? 'error' : 'pending'}
                    label={r.last_push_status || 'never'}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

const PriceListViewer = () => {
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('profile_id') ? 'profile' : 'customer');

  // ── Customer view state ──
  const [company, setCompany] = useState(searchParams.get('company') || 'AU');
  const [customer, setCustomer] = useState(null);
  const [context, setContext] = useState(null);
  const [bcStatus, setBcStatus] = useState(null);
  const [custPrices, setCustPrices] = useState(null);
  const [custSearch, setCustSearch] = useState('');
  const custQuery = useDebouncedValue(custSearch);
  const [custPage, setCustPage] = useState(1);
  const [custLoading, setCustLoading] = useState(false);
  const [custError, setCustError] = useState(null);

  // ── Profile view state ──
  const [profiles, setProfiles] = useState([]);
  const [profileId, setProfileId] = useState(searchParams.get('profile_id') || '');
  const [profPrices, setProfPrices] = useState(null);
  const [profCustomers, setProfCustomers] = useState(null);
  const [profSearch, setProfSearch] = useState('');
  const profQuery = useDebouncedValue(profSearch);
  const [profPage, setProfPage] = useState(1);
  const [profLoading, setProfLoading] = useState(false);
  const [profError, setProfError] = useState(null);

  const searchCustomers = useCallback(
    (q) => searchPricingCustomers(company, q).then((d) => d.customers || []),
    [company],
  );

  // Deep link: /pricing/price-lists?company=AU&cust_num=123
  useEffect(() => {
    const custNum = searchParams.get('cust_num');
    const co = searchParams.get('company') || 'AU';
    if (custNum && !customer) {
      getCustomerPricingContext(co, Number(custNum))
        .then((ctx) => {
          setCustomer({
            cust_num: ctx.customer.cust_num,
            cust_id: ctx.customer.cust_id,
            name: ctx.customer.name,
          });
        })
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const switchCompany = (c) => {
    setCompany(c);
    setCustomer(null);
    setContext(null);
    setBcStatus(null);
    setCustPrices(null);
    setCustError(null);
    setCustPage(1);
    setCustSearch('');
  };

  // Load the customer's context + BC status + first price page on pick.
  useEffect(() => {
    if (!customer) {
      setContext(null);
      setBcStatus(null);
      setCustPrices(null);
      return;
    }
    setCustError(null);
    getCustomerPricingContext(company, customer.cust_num)
      .then(setContext)
      .catch(() => setContext(null));
    getCustomerPriceListStatus(company, customer.cust_num)
      .then(setBcStatus)
      .catch(() => setBcStatus(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer, company]);

  useEffect(() => {
    if (!customer) return;
    setCustLoading(true);
    const params = { page: custPage, page_size: PAGE_SIZE };
    if (custQuery.trim()) params.search = custQuery.trim();
    listCustomerPrices(company, customer.cust_num, params)
      .then((d) => { setCustPrices(d); setCustError(null); })
      .catch((err) => {
        setCustPrices(null);
        setCustError(err.response?.data?.detail || err.message);
      })
      .finally(() => setCustLoading(false));
  }, [customer, company, custPage, custQuery]);

  // Profile list for the picker.
  useEffect(() => {
    listPricingProfiles({ limit: 1000 })
      .then((d) => setProfiles(d.profiles || []))
      .catch(() => setProfiles([]));
  }, []);

  useEffect(() => {
    if (!profileId) {
      setProfPrices(null);
      setProfCustomers(null);
      return;
    }
    setProfLoading(true);
    const params = { page: profPage, page_size: PAGE_SIZE };
    if (profQuery.trim()) params.search = profQuery.trim();
    Promise.all([
      listProfilePrices(profileId, params),
      profPage === 1 ? listProfileCustomers(profileId) : Promise.resolve(profCustomers),
    ])
      .then(([prices, customers]) => {
        setProfPrices(prices);
        setProfCustomers(customers);
        setProfError(null);
      })
      .catch((err) => {
        setProfPrices(null);
        setProfError(err.response?.data?.detail || err.message);
      })
      .finally(() => setProfLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileId, profPage, profQuery]);

  const selectedProfile = profiles.find((p) => String(p.id) === String(profileId));

  return (
    <div className="sync-page price-list-viewer">
      <PageHeader
        title="Price Lists"
        sub="Browse the full resolved price list for one customer (what their BigCommerce price list will contain), or inspect a pricing profile and its member customers. Read-only."
        crumbs={[{ label: 'Pricing', to: '/pricing' }, { label: 'Price Lists' }]}
        actions={<Button variant="secondary" to="/pricing/lookup">Single price lookup</Button>}
      />

      <Tabs tabs={TABS} active={activeTab} onChange={setActiveTab} label="Price list views" />

      {/* ── Customer view ── */}
      <TabPanel id="customer" active={activeTab}>
        <Card>
          <div className="form-grid" style={{ alignItems: 'flex-end' }}>
            <div className="form-group">
              <span className="form-label">Company</span>
              <div className="checkbox-row">
                {['AU', 'NZ'].map((c) => (
                  <label key={c} className="chip">
                    <input
                      type="radio"
                      name="plv-company"
                      checked={company === c}
                      onChange={() => switchCompany(c)}
                    />
                    {c}
                  </label>
                ))}
              </div>
            </div>
            <div className="form-group" style={{ minWidth: 340 }}>
              <label className="form-label">Customer (Epicor ID or name)</label>
              <TypeAhead
                placeholder="e.g. ABC100 or ABC Pool"
                search={searchCustomers}
                display={(c) => `${c.name} (${c.cust_id})`}
                pickedLabel={customer ? `${customer.name} (${customer.cust_id})` : ''}
                onPick={(c) => { setCustPage(1); setCustSearch(''); setCustomer(c); }}
                renderOption={(c) => (
                  <span>
                    <strong>{c.name}</strong>{' '}
                    <span className="muted">
                      {c.cust_id} · #{c.cust_num} · {c.group_code || 'no group'}
                      {c.has_profile ? '' : ' · ⚠ no pricing profile'}
                    </span>
                  </span>
                )}
              />
            </div>
          </div>

          {context?.customer && (
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center', marginTop: 10 }}>
              <Badge variant={context.customer.inactive ? 'error' : 'success'}>
                {context.customer.inactive ? 'Inactive' : 'Active'}
              </Badge>
              <Badge variant="info">Group {context.customer.group_code || '—'}</Badge>
              <Badge variant={context.customer.profile_id ? 'success' : 'warning'}>
                Profile: {context.customer.profile_label || 'unresolved'}
              </Badge>
              <Badge variant="neutral">
                {context.customer.currency_code || (company === 'NZ' ? 'NZD' : 'AUD')}
              </Badge>
              {context.customer.has_shipto_lists && (
                <Badge variant="warning">Has ship-to specific lists</Badge>
              )}
              {context.customer.profile_customer_count > 1 && (
                <span className="muted">
                  Shares identical pricing with {fmt(context.customer.profile_customer_count - 1)} other customer(s)
                </span>
              )}
            </div>
          )}

          {context?.customer?.inactive && (
            <p className="error-text" style={{ margin: '10px 0 0' }}>
              This customer is marked Inactive in Epicor and is excluded from price list pushes.
            </p>
          )}

          {customer && <BcStatusStrip status={bcStatus} />}
        </Card>

        {custError && (
          <Card><p className="error-text" style={{ margin: 0 }}>{custError}</p></Card>
        )}

        {customer && !custError && (
          <Card title={`Resolved price list — ${customer.name}`}>
            <PriceTable
              data={custPrices}
              page={custPage}
              setPage={setCustPage}
              search={custSearch}
              setSearch={setCustSearch}
              loading={custLoading}
            />
          </Card>
        )}
      </TabPanel>

      {/* ── Profile view ── */}
      <TabPanel id="profile" active={activeTab}>
        <Card>
          <div className="form-group" style={{ maxWidth: 620 }}>
            <label className="form-label">Pricing profile</label>
            <select
              value={profileId}
              onChange={(e) => { setProfPage(1); setProfSearch(''); setProfileId(e.target.value); }}
              style={{ width: '100%' }}
            >
              <option value="">Pick a profile…</option>
              {profiles.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.company} — {p.label || p.fingerprint.slice(0, 8)} · {p.customer_count} customer(s) · {p.resolved_parts} parts
                </option>
              ))}
            </select>
          </div>
          {selectedProfile && (
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center', marginTop: 6 }}>
              <Badge variant="info">{selectedProfile.company}</Badge>
              <Badge variant="neutral">{selectedProfile.currency_code || '—'}</Badge>
              <span className="muted">
                fingerprint {selectedProfile.fingerprint?.slice(0, 8)} · resolved {fmtTime(selectedProfile.resolved_at)}
              </span>
            </div>
          )}
        </Card>

        {profError && (
          <Card><p className="error-text" style={{ margin: 0 }}>{profError}</p></Card>
        )}

        {profileId && !profError && (
          <>
            <Card title="Member customers" desc="Each mapped customer gets their OWN BigCommerce price list (1:1); the records are identical within a profile.">
              <table>
                <thead>
                  <tr>
                    <th>Customer</th>
                    <th>Epicor ID</th>
                    <th>Status</th>
                    <th>Group</th>
                    <th>BC customer</th>
                    <th>BC price list</th>
                    <th>Last pushed</th>
                  </tr>
                </thead>
                <tbody>
                  {!profCustomers?.customers?.length && (
                    <tr><td colSpan={7} className="muted">No member customers.</td></tr>
                  )}
                  {(profCustomers?.customers || []).map((c) => (
                    <tr key={c.cust_num}>
                      <td><strong>{c.name || `#${c.cust_num}`}</strong></td>
                      <td>{c.cust_id || '—'}</td>
                      <td>
                        {c.inactive || c.is_present === false
                          ? <Badge variant="error">Inactive</Badge>
                          : <Badge variant="success">Active</Badge>}
                      </td>
                      <td>{c.group_code || '—'}</td>
                      <td>
                        {c.bc_customer_id
                          ? <Badge variant="success">#{c.bc_customer_id}</Badge>
                          : <Badge variant="warning">not mapped</Badge>}
                      </td>
                      <td>{c.bc_price_list_id ?? '—'}</td>
                      <td>{fmtTime(c.last_pushed_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>

            <Card title="Resolved prices">
              <PriceTable
                data={profPrices}
                page={profPage}
                setPage={setProfPage}
                search={profSearch}
                setSearch={setProfSearch}
                loading={profLoading}
              />
            </Card>
          </>
        )}
      </TabPanel>
    </div>
  );
};

export default PriceListViewer;
